import { LitElement, html, nothing } from 'lit';
import { property, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { guardedCustomElement } from '../utils/guarded-custom-element';
import { styles } from './sds-textarea.styles';
import { SDSDelegateInteractionMixin, SDSEventDispatcherMixin } from '../mixins';

import '../sds-alert/sds-alert';
import '../sds-icon/sds-icon';
import { SDS_SIZES } from '../types';

type InputSize = Extract<SDS_SIZES, 'lg' | 'md' | 'sm'>;
export interface SDSTextAreaProps {
	value?: string;
	name?: string;
	label?: string;
	placeholder?: string;
	'help-text'?: string;
	'error-message'?: string;
	disabled?: boolean;
	readonly?: boolean;
	autocomplete?: HTMLTextAreaElement['autocomplete'];
	required?: boolean;
	rows?: number;
	cols?: number;
	wrap?: 'hard' | 'soft';
	minlength?: number;
	maxlength?: number;
	counter?: number;
	size?: InputSize;
}

@guardedCustomElement('sds-textarea')
export class SDSTextArea extends SDSDelegateInteractionMixin(SDSEventDispatcherMixin(LitElement)) {
	// Styling
	static override styles = [styles];

	@property({ type: String }) value?: string;
	@property({ type: String, reflect: true }) name?: SDSTextAreaProps['name'];
	@property({ type: String, reflect: true }) label?: SDSTextAreaProps['label'];
	@property({ type: String, reflect: true }) placeholder?: SDSTextAreaProps['placeholder'];
	@property({ type: String, attribute: 'help-text', reflect: true }) helpText?: SDSTextAreaProps['help-text'];
	@property({ type: String, attribute: 'error-message', reflect: true }) errorMessage?: SDSTextAreaProps['error-message'];
	@property({ type: Boolean, reflect: true }) disabled: SDSTextAreaProps['disabled'] = false;
	@property({ type: Boolean, attribute: 'readonly', reflect: true }) readOnly: SDSTextAreaProps['readonly'] = false;
	@property({ type: String, reflect: true }) autocomplete?: SDSTextAreaProps['autocomplete'];
	@property({ type: Boolean, reflect: true }) required: SDSTextAreaProps['required'] = false;
	@property({ type: Number, reflect: true }) rows?: SDSTextAreaProps['rows'];
	@property({ type: Number, reflect: true }) cols?: SDSTextAreaProps['cols'];
	@property({ type: String, reflect: true }) wrap?: SDSTextAreaProps['wrap'];
	@property({ type: Number, reflect: true }) minlength?: SDSTextAreaProps['minlength'];
	@property({ type: Number, reflect: true }) maxlength?: SDSTextAreaProps['maxlength'];
	@property({ type: Number, reflect: true }) counter?: SDSTextAreaProps['counter'];
	@property({ type: String, reflect: true }) size: SDSTextAreaProps['size'] = 'md';

	@query('#sds-textarea') control!: HTMLTextAreaElement;

	static formAssociated = true;
	public readonly internals: ElementInternals;

	static override shadowRootOptions: ShadowRootInit = {
		...LitElement.shadowRootOptions,
		delegatesFocus: true,
	};

	private _defaultValue: string | null = null;

	constructor() {
		super();
		this.internals = this.attachInternals();
	}

	override connectedCallback() {
		super.connectedCallback();
		this._defaultValue = this.value ?? null;
		this.internals.setFormValue(this._defaultValue);
	}

	override updated(changedProperties: Map<string | number | symbol, unknown>) {
		super.updated(changedProperties);

		if (changedProperties.has('value')) {
			this.internals.setFormValue(this.value ?? '');

			if (this.control) {
				this.control.value = this.value ?? '';
			}
		}
	}

	private updateValidity() {
		if (this.control.checkValidity()) {
			this.internals.setValidity({});
		} else {
			this.internals.setValidity({ customError: true }, this.control.validationMessage, this.control);
		}
	}

	private hasTypedTooManyCharacters() {
		if (!this.counter) {
			return false;
		}
		const currentLength = this.value?.length || 0;
		return currentLength > this.counter;
	}

	private renderCharacterCounter() {
		if (this.counter === undefined) return nothing;

		const currentLength = this.value?.length || 0;
		if (this.hasTypedTooManyCharacters()) {
			return html`<sds-alert variant="secondary" data-palette="danger"
				><p class="sds-textarea__character-counter" slot="message">Du har ${currentLength - this.counter} tegn for mye</p></sds-alert
			>`;
		}

		return html`<p class="sds-textarea__character-counter">Du har ${this.counter - currentLength} tegn igjen</p>`;
	}

	private readonly _handleInputChange = (event: InputEvent) => {
		event.stopImmediatePropagation();
		if (this.disabled || !(event.target instanceof HTMLTextAreaElement)) {
			return;
		}

		this.value = event.target.value;
		this.internals.setFormValue(this.value === '' ? null : this.value);
		this.updateValidity();
		this.dispatchSheriffEvent(event.type);
	};

	private readonly _handleSelect = (event: Event) => {
		event.stopImmediatePropagation();
		if (!this.disabled) {
			this.dispatchSheriffEvent('select');
		}
	};

	private readonly _handleFocus = (event: FocusEvent) => {
		event.stopImmediatePropagation();
		this.dispatchSheriffEvent('focus');
	};

	private readonly _handleBlur = (event: FocusEvent) => {
		event.stopImmediatePropagation();
		this.updateValidity();
		this.dispatchSheriffEvent('blur');
	};

	formResetCallback() {
		this.internals.setFormValue(this._defaultValue);

		const newValue = this._defaultValue ?? '';
		this.control.value = newValue;
		this.value = newValue;
		this.updateValidity();
	}

	select(): void {
		if (!this.disabled) this.control.select();
	}

	type(): string {
		return 'textarea';
	}

	get selectionStart(): number | null {
		return this.control ? this.control.selectionStart : null;
	}

	set selectionStart(value: number | null) {
		if (this.control && value !== null) {
			this.control.selectionStart = value;
		}
	}

	get selectionEnd(): number | null {
		return this.control ? this.control.selectionEnd : null;
	}
	set selectionEnd(value: number | null) {
		if (this.control && value !== null) {
			this.control.selectionEnd = value;
		}
	}

	setSelectionRange(start: number, end: number, direction?: 'forward' | 'backward' | 'none'): void {
		if (this.control) {
			this.control.setSelectionRange(start, end, direction);
		}
	}

	checkValidity(): boolean {
		return this.control.checkValidity();
	}

	reportValidity(): boolean {
		return this.control.reportValidity();
	}

	setCustomValidity(message: string): void {
		this.control.setCustomValidity(message);
		this.updateValidity();
	}

	override render() {
		const textareaClasses = {
			'sds-textarea': true,
			'sds-textarea--error': Boolean(this.errorMessage || this.hasTypedTooManyCharacters()),
		};

		return html`
			<div class="${classMap(textareaClasses)}">
				${this.label || this.helpText
					? html`<div>
							${this.label ? html`<label for="sds-textarea" class="sds-textarea__label">${this.label}</label>` : nothing}
							${this.helpText ? html`<p class="sds-textarea__help-text">${this.helpText}</p>` : nothing}
						</div>`
					: nothing}
				<div class="sds-textarea__input-container">
					<textarea
						class="sds-textarea__input"
						id="sds-textarea"
						?disabled="${this.disabled}"
						?readonly="${this.readOnly}"
						placeholder="${ifDefined(this.placeholder)}"
						.value="${this.value ?? ''}"
						.autocomplete="${this.autocomplete}"
						name="${ifDefined(this.name)}"
						?required="${this.required}"
						rows="${ifDefined(this.rows)}"
						cols="${ifDefined(this.cols)}"
						wrap="${ifDefined(this.wrap)}"
						minlength="${ifDefined(this.minlength)}"
						maxlength="${ifDefined(this.maxlength)}"
						@input="${this._handleInputChange}"
						@change="${this._handleInputChange}"
						@select="${this._handleSelect}"
						@focus="${this._handleFocus}"
						@blur="${this._handleBlur}"
					></textarea>
				</div>
				${this.errorMessage ? html`<sds-alert variant="secondary" data-palette="danger"><p slot="message">${this.errorMessage}</p></sds-alert>` : nothing} ${this.renderCharacterCounter()}
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'sds-textarea': SDSTextArea;
	}
}
